// Phase JA.2 — Wired Signal Menu panel.
//
// Owns: hook fetch + URL-param filter state. Renders the FilterBar +
// SignalMenu pair. Drops into the existing DailyAccaPage shell as a
// single slot — does NOT alter the I0 primitives or the page chrome.

import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useDailyAccaSignals } from "../../free-tier/useDailyAccaSignals.ts";
import { useEscalatingMenu } from "../../conversion/useEscalatingMenu.ts";
import { trackEvent } from "../../conversion/trackEvent.ts";
import {
  applyFilters,
  filtersFromParams,
  filtersToParams,
  nextPublishISO,
  topLeagues,
  type SignalMenuFilters,
} from "./signalMenuFilters.ts";
import { SignalFilterBar } from "./SignalFilterBar.tsx";
import { SignalMenu } from "./SignalMenu.tsx";

export interface SignalMenuPanelProps {
  /** When true, render the soft anon-only upsell on tier-A rows. */
  showAnonUpsell?: boolean;
  /** Phase JA.4 — page-level handler for "+ ADD TO SLIP" affordance. */
  onAddToSlip?: (signal: import("../../free-tier/useDailyAccaSignals.ts").DailyAccaSignal) => void;
  /** Phase JA.4 — set of signal_ids in the user's draft. */
  draftSignalIds?: ReadonlySet<string>;
}

export function SignalMenuPanel({
  showAnonUpsell,
  onAddToSlip,
  draftSignalIds,
}: SignalMenuPanelProps): JSX.Element {
  const { signals, loading, error } = useDailyAccaSignals();
  const { tierAUnlocks, copy: escalatingCopy } = useEscalatingMenu();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<SignalMenuFilters>(
    () => filtersFromParams((k) => searchParams.get(k)),
    [searchParams],
  );

  const handleChange = (next: SignalMenuFilters): void => {
    trackEvent("signal_filtered", { filters: next });
    const params = filtersToParams(next);
    // Preserve other unrelated query params already on the URL.
    const merged = new URLSearchParams(searchParams);
    for (const k of ["tier", "cat", "lg", "minc"]) merged.delete(k);
    for (const [k, v] of Object.entries(params)) merged.set(k, v);
    setSearchParams(merged, { replace: true });
  };

  const filtered = useMemo(() => applyFilters(signals, filters), [signals, filters]);

  // signal_viewed: fire once per signal per page load.
  const seenIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    let glimpsed = 0;
    for (const s of filtered) {
      if (!seenIdsRef.current.has(s.signal_id)) {
        seenIdsRef.current.add(s.signal_id);
        trackEvent("signal_viewed", { signal_id: s.signal_id, tier: s.tier });
        if (showAnonUpsell && s.tier === "tier_a") glimpsed += 1;
      }
    }
    if (glimpsed > 0) {
      trackEvent("pro_signal_glimpsed", { count: glimpsed });
    }
  }, [filtered, showAnonUpsell]);

  const trackedAdd = onAddToSlip
    ? (sig: import("../../free-tier/useDailyAccaSignals.ts").DailyAccaSignal): void => {
        trackEvent("signal_clicked", { signal_id: sig.signal_id, tier: sig.tier });
        trackEvent("slip_leg_added", { signal_id: sig.signal_id });
        onAddToSlip(sig);
      }
    : undefined;
  const leagues = useMemo(() => topLeagues(signals), [signals]);
  const publishISO = useMemo(() => nextPublishISO(), []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {error !== null ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--sa-signal-red)",
            background: "var(--sa-signal-red-dim)",
            borderRadius: 6,
            fontFamily: "var(--sa-font-body)",
            color: "var(--sa-fg-primary)",
            fontSize: 13,
          }}
        >
          <strong>Could not load signals.</strong> {error}
        </div>
      ) : null}
      {signals.length > 0 ? (
        <SignalFilterBar
          filters={filters}
          onChange={handleChange}
          leagueOptions={leagues}
        />
      ) : null}
      <SignalMenu
        signals={filtered}
        loading={loading}
        showAnonUpsell={showAnonUpsell}
        nextPublishISO={publishISO}
        onAddToSlip={trackedAdd}
        draftSignalIds={draftSignalIds}
        tierAUnlocks={tierAUnlocks}
        unlockCopy={escalatingCopy}
      />
    </div>
  );
}
